import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import MgmtLayout from '@/components/feature/MgmtLayout';
import { useManagementProjectsLite } from '@/hooks/useManagementProjectsLite';
import { getProjectCoverImage } from '@/lib/projectCover';
import { formatDate } from '@/lib/dateUtils';

type FilterValue = 'All' | 'Active' | 'Planning' | 'On Hold' | 'Complete';

const statusColor: Record<string, string> = {
  active: '#22C55E',
  planning: '#F59E0B',
  on_hold: '#EF4444',
  complete: '#059669',
};

const statusBg: Record<string, string> = {
  active: 'rgba(34,197,94,0.15)',
  planning: 'rgba(245,158,11,0.15)',
  on_hold: 'rgba(239,68,68,0.15)',
  complete: 'rgba(5,150,105,0.15)',
};

const statusLabel: Record<string, string> = {
  active: 'Active',
  planning: 'Planning',
  on_hold: 'On Hold',
  complete: 'Complete',
};

function getCoverImage(project: { project_type: string | null; cover_image: string | null; cover_photo_url?: string | null }): string {
  return getProjectCoverImage(project);
}

export default function ManagementProjectList() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeFilter, setActiveFilter] = useState<FilterValue>('All');
  const [search, setSearch] = useState('');
  const [slowLoadWarning, setSlowLoadWarning] = useState(false);

  const { projects, loading, error, refresh } = useManagementProjectsLite();

  useEffect(() => {
    if (!loading || projects.length > 0 || error) {
      setSlowLoadWarning(false);
      return;
    }
    const timer = setTimeout(() => {
      if (loading && projects.length === 0 && !error) {
        setSlowLoadWarning(true);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [loading, projects.length, error]);

  const filters: { label: string; value: FilterValue }[] = [
    { label: t('instructions.filterAll'), value: 'All' },
    { label: t('status.active'), value: 'Active' },
    { label: t('status.planning'), value: 'Planning' },
    { label: t('status.onHold'), value: 'On Hold' },
    { label: t('status.complete'), value: 'Complete' },
  ];

  const filtered = projects.filter((p) => {
    const matchesFilter =
      activeFilter === 'All' ||
      (activeFilter === 'Active' && p.calculatedStatus === 'active') ||
      (activeFilter === 'Planning' && p.calculatedStatus === 'planning') ||
      (activeFilter === 'On Hold' && p.calculatedStatus === 'on_hold') ||
      (activeFilter === 'Complete' && p.calculatedStatus === 'complete');

    const matchesSearch =
      !search ||
      p.project_name.toLowerCase().includes(search.toLowerCase()) ||
      (p.site_address || '').toLowerCase().includes(search.toLowerCase()) ||
      (p.client_name || '').toLowerCase().includes(search.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  return (
    <MgmtLayout
      title={t('projects.title')}
      actions={
        <button
          onClick={() => navigate('/management/projects/new')}
          className="flex items-center gap-1.5 bg-[#E8621A] text-white px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap hover:bg-[#B84C10] transition-colors"
        >
          <i className="ri-add-line text-base"></i>
          <span className="hidden sm:inline">{t('btn.newProject')}</span>
          <span className="sm:hidden">{t('btn.new')}</span>
        </button>
      }
    >
      <div className="px-4 lg:px-8 pt-5 pb-6 overflow-x-hidden">
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-[#9CA3AF] text-base"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('projects.search')}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-[#1A1A1A] focus:outline-none focus:border-[#E8621A] transition-colors"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {filters.map((f) => (
              <button
                key={f.value}
                onClick={() => setActiveFilter(f.value)}
                className={`flex-shrink-0 px-3.5 py-2 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                  activeFilter === f.value
                    ? 'bg-[#E8621A] text-white'
                    : 'bg-white border border-gray-200 text-[#6B7280] hover:border-[#E8621A]/40'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <p className="text-[#9CA3AF] text-xs mb-4">
          {filtered.length} {filtered.length !== 1 ? t('projects.projects') : t('projects.project')}
          {search && ` ${t('projects.matching')} "${search}"`}
        </p>

        {slowLoadWarning && loading && projects.length === 0 && !error && (
          <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
            <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-time-line text-amber-600 text-sm"></i>
            </div>
            <div className="flex-1 text-left">
              <p className="text-amber-800 text-sm font-semibold">
                {t('dashboard.loadingSlow', 'Projects are taking longer than expected. Please retry.')}
              </p>
              <button onClick={refresh} className="text-amber-700 text-xs font-medium mt-1 hover:underline cursor-pointer">
                {t('btn.retry') || 'Retry'}
              </button>
            </div>
          </div>
        )}

        {loading && projects.length === 0 && !error && (
          <div className="text-center py-20">
            <i className="ri-loader-4-line animate-spin text-[#E8621A] text-3xl"></i>
            <p className="text-[#9CA3AF] text-sm mt-3">{t('projects.loadingProjects')}</p>
          </div>
        )}

        {error && projects.length === 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 text-sm mt-0.5"></i>
              <div>
                <p className="text-red-600 text-sm font-medium">{error}</p>
                <button
                  onClick={refresh}
                  className="text-red-500 text-xs font-medium mt-1 cursor-pointer hover:underline"
                >
                  {t('btn.tryAgain')}
                </button>
              </div>
            </div>
          </div>
        )}

        {projects.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {filtered.map((project) => {
              const progress = project.calculatedProgress;
              const currentStage = project.calculatedStage;
              const coverImage = getCoverImage(project);
              const color = statusColor[project.calculatedStatus] || '#9CA3AF';
              const bg = statusBg[project.calculatedStatus] || 'rgba(156,163,175,0.15)';
              const label = statusLabel[project.calculatedStatus] || project.calculatedStatus;

              return (
                <button
                  key={project.id}
                  onClick={() => navigate(`/management/projects/${project.id}`)}
                  className="bg-white rounded-2xl border border-gray-100 overflow-hidden text-left hover:border-[#E8621A]/40 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer flex flex-col"
                >
                  <div className="h-52 relative overflow-hidden flex-shrink-0">
                    <img
                      src={coverImage}
                      alt={project.project_name}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#1A1A1A]/80 via-[#1A1A1A]/10 to-transparent"></div>
                    <div className="absolute top-3 left-3 flex items-center gap-1.5">
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-black/30 text-white backdrop-blur-sm border border-white/10">
                        {project.project_code}
                      </span>
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: bg, color }}
                      >
                        ● {label}
                      </span>
                    </div>
                    <div className="absolute bottom-3 left-3 right-3">
                      <div className="text-white font-bold text-[15px] leading-tight">{project.project_name}</div>
                      <div className="text-white/65 text-xs mt-0.5 truncate">{project.site_address || t('projects.noAddress')}</div>
                    </div>
                  </div>

                  <div className="px-4 py-3 flex-1 flex flex-col justify-between gap-2.5">
                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[#6B7280] text-xs font-medium">{currentStage}</span>
                        <span className="text-xs font-bold" style={{ color: progress === 100 ? '#059669' : '#E8621A' }}>{progress}%</span>
                      </div>
                      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all"
                          style={{ width: `${progress}%`, backgroundColor: progress === 100 ? '#059669' : '#E8621A' }}
                        ></div>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[9px] text-[#9CA3AF]">
                          {t('projects.schedFinal')}: <span className="font-bold" style={{ color: project.scheduleFinalPct === 100 ? '#059669' : '#E8621A' }}>{project.scheduleFinalPct}%</span>
                        </span>
                        <span className="text-[9px] text-[#D1D5DB]">·</span>
                        <span className="text-[9px] text-[#9CA3AF]">
                          {t('projects.clFinal')}: <span className="font-bold" style={{ color: project.checklistFinalPct === 100 ? '#059669' : '#E8621A' }}>{project.checklistFinalPct}%</span>
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#FEF0E7] text-[#E8621A] font-medium">
                        {project.project_type}
                      </span>
                    </div>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <i className="ri-user-line text-[10px] text-[#9CA3AF]"></i>
                        <span className="text-[#6B7280] text-xs truncate">{project.client_name || t('projects.noClient')}</span>
                      </div>
                      <span className="text-[#374151] text-xs font-semibold">
                        {formatDate(project.start_date)}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && projects.length > 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-[#D1D5DB]">
              <i className="ri-search-line text-6xl"></i>
            </div>
            <div className="text-[#374151] font-semibold mb-1">{t('projects.noProjectsFound') || 'No projects match your filter'}</div>
            <div className="text-[#9CA3AF] text-sm">
              {search ? t('projects.tryAdjusting') || 'Try adjusting your search or filters' : t('projects.noMatchFilter') || 'No projects match the current filter'}
            </div>
            {search && (
              <button
                onClick={() => setSearch('')}
                className="mt-4 text-[#E8621A] text-sm font-medium cursor-pointer"
              >
                {t('projects.clearSearch') || 'Clear search'}
              </button>
            )}
            <button
              onClick={() => setActiveFilter('All')}
              className="mt-2 ml-2 text-[#E8621A] text-sm font-medium cursor-pointer"
            >
              {t('projects.showAll') || 'Show all'}
            </button>
          </div>
        )}

        {!loading && !error && projects.length === 0 && (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-[#D1D5DB]">
              <i className="ri-building-2-line text-6xl"></i>
            </div>
            <div className="text-[#374151] font-semibold mb-1">{t('dashboard.noProjects') || 'No projects found'}</div>
            <div className="text-[#9CA3AF] text-sm mb-4">{t('dashboard.createFirst') || 'Get started by creating your first project'}</div>
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={refresh}
                className="inline-flex items-center gap-2 bg-white border border-gray-200 text-[#374151] px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap hover:border-[#E8621A]/40 transition-colors"
              >
                <i className="ri-refresh-line"></i>
                {t('btn.retry') || 'Retry'}
              </button>
              <button
                onClick={() => navigate('/management/projects/new')}
                className="inline-flex items-center gap-2 bg-[#E8621A] text-white px-5 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                {t('dashboard.createFirstProject') || 'Create first project'}
              </button>
            </div>
          </div>
        )}
      </div>
    </MgmtLayout>
  );
}
